import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'

import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * The admin Button, scaled for a till. Same pill shape, black primary and grey secondary, but sized for thumbs:
 * `touch` (44px) for row actions, `xl` (56px) for the one action that ends a sale.
 */
const posButtonVariants = cva(
  'inline-flex shrink-0 select-none items-center justify-center gap-2 whitespace-nowrap rounded-sm font-medium transition-[color,background-color,transform] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-40 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-button hover:bg-primary-hover',
        secondary: 'bg-fill text-ink hover:bg-fill-hover',
        outline: 'border border-input bg-card text-ink hover:bg-subdued',
        ghost: 'text-ink hover:bg-fill',
        success: 'bg-success text-white shadow-button hover:bg-success-dark',
        destructive: 'bg-destructive text-destructive-foreground shadow-button hover:bg-error-dark',
      },
      size: {
        default: 'h-9 px-4 text-sm [&_svg]:size-4',
        touch: 'h-11 px-5 text-sm [&_svg]:size-[18px]',
        xl: 'h-14 px-6 text-lg font-semibold [&_svg]:size-5',
        icon: 'size-9 [&_svg]:size-4',
        'icon-touch': 'size-11 [&_svg]:size-5',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface PosButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof posButtonVariants> {
  asChild?: boolean
}

export const PosButton = React.forwardRef<HTMLButtonElement, PosButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp ref={ref} className={cn(posButtonVariants({ variant, size, className }))} {...props} />
  }
)
PosButton.displayName = 'PosButton'
